import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Space, Card, Tag, Tooltip, Modal, message, Input, Select, Dropdown } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
  MoreOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import AdRuleAPIService from '../../services/AdRuleAPIService';
import { adsPermissions, workflowPermissions } from '../../../../utils/permissions';

// Global ResizeObserver error suppression
const suppressResizeObserverError = () => {
  const originalError = console.error;
  console.error = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
      return;
    }
    if (args[0] && args[0].message && args[0].message.includes('ResizeObserver')) {
      return;
    }
    originalError.apply(console, args);
  };

  // Override window error handler
  const originalWindowError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (message && message.includes('ResizeObserver')) {
      return true; // Prevent the error from being logged
    }
    if (originalWindowError) {
      return originalWindowError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  // Override unhandled promise rejection
  const originalUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver')) {
      event.preventDefault();
      return;
    }
    if (originalUnhandledRejection) {
      originalUnhandledRejection.call(window, event);
    }
  };
};

// Call it immediately
suppressResizeObserverError();

const { Search } = Input;
const { Option } = Select;

const RuleList = ({ onCreateRule, onEditRule, onRunRule, onViewFlow, refreshTrigger = 0 }) => {
  const { user } = useSelector(state => state.auth);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredRules, setFilteredRules] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    type: undefined,
    platform: undefined,
    isActive: undefined
  });

  // Load rules from API - memoized
  const loadRules = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        ...params
      };

      const response = await AdRuleAPIService.getRules(queryParams);

      if (response.success) {
        setRules(response.data.rules);
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
          current: response.data.page
        }));
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error loading rules:', error);
      message.error('Lỗi khi tải danh sách rules: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  // Load rules on component mount and filter changes
  useEffect(() => {
    loadRules();
  }, [loadRules, refreshTrigger]);

  // Filter rules based on search - memoized
  const filteredRulesData = useMemo(() => {
    if (searchText) {
      return rules.filter(rule =>
        rule.name.toLowerCase().includes(searchText.toLowerCase()) ||
        rule.description?.toLowerCase().includes(searchText.toLowerCase()) ||
        rule.type?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    return rules;
  }, [searchText, rules]);

  // Update filtered rules when data changes
  useEffect(() => {
    setFilteredRules(filteredRulesData);
  }, [filteredRulesData]);

  const handleDelete = useCallback(async (ruleId) => {
    Modal.confirm({
      title: 'Xóa Rule',
      content: 'Bạn có chắc chắn muốn xóa rule này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      async onOk() {
        try {
          const response = await AdRuleAPIService.deleteRule(ruleId);
          if (response.success) {
            message.success('Xóa rule thành công');
            loadRules(); // Reload rules
          } else {
            throw new Error(response.message);
          }
        } catch (error) {
          console.error('Error deleting rule:', error);
          message.error('Lỗi khi xóa rule: ' + error.message);
        }
      }
    });
  }, [loadRules]);

  const handleStatusToggle = useCallback(async (ruleId) => {
    try {
      const response = await AdRuleAPIService.toggleRuleStatus(ruleId);
      if (response.success) {
        message.success(response.message);
        loadRules(); // Reload rules
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error toggling rule status:', error);
      message.error('Lỗi khi thay đổi trạng thái rule: ' + error.message);
    }
  }, [loadRules]);

  const handleDuplicate = useCallback(async (ruleId) => {
    try {
      const response = await AdRuleAPIService.duplicateRule(ruleId);
      if (response.success) {
        message.success('Nhân bản rule thành công');
        loadRules(); // Reload rules
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error duplicating rule:', error);
      message.error('Lỗi khi nhân bản rule: ' + error.message);
    }
  }, [loadRules]);

  const getStatusColor = (isActive) => {
    return isActive ? 'green' : 'red';
  };

  const getTypeLabel = (type) => {
    const types = AdRuleAPIService.getRuleTypes();
    const typeObj = types.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getPlatformLabel = (platform) => {
    const platforms = AdRuleAPIService.getPlatforms();
    const platformObj = platforms.find(p => p.value === platform);
    return platformObj ? platformObj.label : platform;
  };

  // Memoize table columns to prevent unnecessary re-renders
  const columns = useMemo(() => [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_, __, index) => (
        (pagination.current - 1) * pagination.pageSize + index + 1
      )
    },
    {
      title: 'Tên Rule',
      dataIndex: 'name',
      key: 'name',
      width: 320,
      render: (text, record) => (
        <span 
          style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }}
          onClick={() => onViewFlow?.(record)}
        >
          {text}
        </span>
      )
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <span style={{ fontSize: '13px', color: '#666' }}>
          {text || '-'}
        </span>
      )
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color="blue">{getTypeLabel(type)}</Tag>
      )
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (platform) => (
        <Tag color="orange">{getPlatformLabel(platform)}</Tag>
      )
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={getStatusColor(isActive)}>
          {isActive ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      )
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('vi-VN')
    },
    {
      title: 'Người tạo',
      dataIndex: 'user',
      key: 'creator',
      width: 140,
      render: (user) => user ? (user.fullName || user.username) : 'N/A'
    },
    {
      title: <MenuOutlined />,
      key: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const menuItems = [];

        // Chỉnh sửa - cần quyền ads.update
        if (adsPermissions.canUpdate(user)) {
          menuItems.push({
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Chỉnh sửa',
            onClick: () => onEditRule?.(record)
          });
        }

        // Xem Workflow - cần quyền workflow.read
        if (workflowPermissions.canRead(user)) {
          menuItems.push({
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Xem Workflow',
            onClick: () => onViewFlow?.(record)
          });
        }

        // Divider nếu có items phía trên
        if (menuItems.length > 0) {
          menuItems.push({ type: 'divider' });
        }

        // Nhân bản - cần quyền ads.duplicate hoặc ads.create
        if (adsPermissions.canDuplicate(user) || adsPermissions.canCreate(user)) {
          menuItems.push({
            key: 'duplicate',
            icon: <CopyOutlined />,
            label: 'Nhân bản',
            onClick: () => handleDuplicate(record.id)
          });
        }

        // Xóa - cần quyền ads.delete
        if (adsPermissions.canDelete(user)) {
          menuItems.push({
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Xóa',
            danger: true,
            onClick: () => handleDelete(record.id)
          });
        }

        // Nếu không có quyền nào, không hiển thị dropdown
        if (menuItems.length === 0 || (menuItems.length === 1 && menuItems[0].type === 'divider')) {
          return null;
        }

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ fontWeight: 'bold' }}
            />
          </Dropdown>
        );
      }
    }
  ], [handleDelete, handleDuplicate, onEditRule, onViewFlow, onRunRule, user]);

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}>
        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <Input
            placeholder="Tìm kiếm theo tên, mô tả..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />

          <Select
            placeholder="Loại rule"
            style={{ width: 160 }}
            allowClear
            value={filters.type}
            onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          >
            {AdRuleAPIService.getRuleTypes().map(type => (
              <Option key={type.value} value={type.value}>{type.label}</Option>
            ))}
          </Select>

          <Select
            placeholder="Platform"
            style={{ width: 140 }}
            allowClear
            value={filters.platform}
            onChange={(value) => setFilters(prev => ({ ...prev, platform: value }))}
          >
            {AdRuleAPIService.getPlatforms().map(platform => (
              <Option key={platform.value} value={platform.value}>{platform.label}</Option>
            ))}
          </Select>

          <Select
            placeholder="Trạng thái"
            style={{ width: 120 }}
            allowClear
            value={filters.isActive}
            onChange={(value) => setFilters(prev => ({ ...prev, isActive: value }))}
          >
            <Option value={true}>Active</Option>
            <Option value={false}>Inactive</Option>
          </Select>
        </div>
        <Space>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => loadRules()}
            loading={loading}
          >
            Làm mới
          </Button>
          {adsPermissions.canCreate(user) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => onCreateRule?.()}
            >
              Tạo Rule mới
            </Button>
          )}
        </Space>
      </div>

      {/* Table */}
      <Card>
        {/* Total Count */}
        <div style={{ marginBottom: 12, fontSize: 14, color: '#666' }}>
          <strong>Total Rules:</strong> {pagination.total > 0
            ? `${(pagination.current - 1) * pagination.pageSize + 1}-${Math.min(pagination.current * pagination.pageSize, pagination.total)} của ${pagination.total}`
            : '0'
          }
        </div>

        <Table
          columns={columns}
          dataSource={filteredRulesData}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} rules`,
            onChange: (page, size) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: size }));
            }
          }}
          scroll={{ x: 1400, y: 600 }}
        />
      </Card>
    </div>
  );
};

export default RuleList;